import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Drawer,
  Radio,
  Select,
  Text,
  Textarea,
} from "@mantine/core";
import swal from "sweetalert";
import useStyles from "./style";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { getColor } from "../../pages/Home/Medication";
import { getActiveSlotForDate } from "../../utils";
import moment from "moment";
import PRNMedicationModal from "../PRNMedicationModal";
import MedicationModal from "../MedicationModal";
import { useSelector } from "react-redux";
import { useForm } from "@mantine/form";
import { createMar, getMar } from "../../services";
import { IconChevronDown, IconClock } from "@tabler/icons-react";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import missedMed from "../../images/missedMed.png";
import upcommingMed from "../../images/upcommingMed.png";
import takenMed from "../../images/takenMed.png";
import refusedMed from "../../images/refusedMed.png";
import outOfMed from "../../images/outOfMed.png";
import unableToTakeMed from "../../images/unableToTakeMed.png";
import prnMed from "../../images/prnMed.png";
import { FiCalendar } from "react-icons/fi";
dayjs.extend(utc);

const getStatusImage = (status) => {
  if (status === "PRN") return prnMed;
  if (status === "Taken") return takenMed;
  if (status === "Refused") return refusedMed;
  if (status === "Unable to Take") return unableToTakeMed;
  if (status === "Out of Medication") return outOfMed;
  if (status === "Missed") return missedMed;
  return upcommingMed;
};

// Helper: resolve the effective status for a MAR entry on a specific date.
// Works for both legacy string medicationType and modern array medicationType.
// For "upcoming" entries on today's date, also checks the timeSlot:
//   - if the slot time has already passed → "Missed" (red)
//   - if the slot time is still ahead    → "upcoming" (gray)
const getMedStatusForDate = (med, date) => {
  const medType = med?.medicationType;
  if (!medType) return _deriveUpcomingOrMissed(med, date);
  if (!Array.isArray(medType)) {
    // legacy string — if it's already "upcoming" re-evaluate with time check
    if (medType === "upcoming") return _deriveUpcomingOrMissed(med, date);
    return medType;
  }

  const targetDateStr = moment(date, [
    "MMMM DD, YYYY",
    "YYYY-MM-DD",
    moment.ISO_8601,
  ]).format("YYYY-MM-DD");
  const entrySlot = getActiveSlotForDate(med.timeSlot, med.date);

  // Exact: same date + same timeSlot
  const exactMatches = medType.filter(
    (m) =>
      moment(m.date).format("YYYY-MM-DD") === targetDateStr &&
      m.timeSlot === entrySlot,
  );
  if (exactMatches.length > 0)
    return exactMatches[exactMatches.length - 1].status;

  // Loose: same date, no timeSlot stored (legacy records)
  const dateOnlyMatches = medType.filter(
    (m) => moment(m.date).format("YYYY-MM-DD") === targetDateStr && !m.timeSlot,
  );
  if (dateOnlyMatches.length > 0)
    return dateOnlyMatches[dateOnlyMatches.length - 1].status;

  // No administration recorded for this date — derive from date+timeslot
  return _deriveUpcomingOrMissed(med, date);
};

// Derives "Missed" or "upcoming" based on date and timeSlot.
// Rule:
//   - Past date                          → "Missed"  (red)
//   - Future date                        → "upcoming" (gray)
//   - Today + timeslot already passed    → "Missed"  (red)
//   - Today + timeslot not yet reached   → "upcoming" (gray)
const _deriveUpcomingOrMissed = (med, date) => {
  const parsedDate = moment(date, [
    "MMMM DD, YYYY",
    "YYYY-MM-DD",
    moment.ISO_8601,
  ]);
  const today = moment().startOf("day");

  if (parsedDate.isBefore(today)) return "Missed";
  if (parsedDate.isAfter(today)) return "upcoming";

  // Same day — check the timeslot
  const slotStr = getActiveSlotForDate(med?.timeSlot, med?.date);
  if (slotStr) {
    const slotTime = moment(slotStr, ["hh:mm A", "HH:mm"]);
    if (slotTime.isValid() && moment().isAfter(slotTime)) return "Missed";
  }
  return "upcoming";
};

const MarMedicationModal = ({
  data,
  date,
  close,
  opened,
  calendar,
  futureDate,
  currentDate,
}) => {
  const { classes } = useStyles();
  const isMobileScreen = useMediaQuery("(max-width: 600px)");
  const [btnLoading, setBtnLoading] = useState(false);
  const { selectedClient, loading: marLoading } = useSelector(
    (state) => state.mar,
  );
  const { profile } = useSelector((state) => state.profile);
  const [selectedMedicine, setSelectedMedicine] = useState([]);
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [filteredWeekData, setFilteredData] = useState();
  const [prnShow, setPRNShow] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const prnRef = useRef();
  const openPRN = () => {
    setIsNew(true);
    setPRNShow(true);
  };
  const prnClose = () => setPRNShow(false);

  const getCalendarDateKey = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return moment(value, "MM/DD/YYYY").format("YYYY-MM-DD");
    }
    if (typeof value === "string" && /^[A-Za-z]+ \d{2}, \d{4}$/.test(value)) {
      return moment(value, "MMMM DD, YYYY").format("YYYY-MM-DD");
    }
    // FIX: Always use local time (moment(), not moment.utc()) for ISO strings and
    // Date objects. Previously moment.utc() was used for strings, which shifted the
    // calendar date by -1 day for UTC+ timezone users (e.g. PKT = UTC+5):
    // a virtual entry at "2025-04-05T20:00:00Z" is April 6 locally but moment.utc()
    // returned "2025-04-05", so the save landed one day behind what the user clicked.
    return moment(value).format("YYYY-MM-DD");
  };

  const getSlotKey = (slot) => {
    if (Array.isArray(slot)) {
      // slot may be array of objects [{time, ...}] or array of plain strings ["08:00 AM"]
      const first = slot[0];
      if (first == null) return null;
      return typeof first === "object" ? (first.time ?? null) : first;
    }
    // single slot object or plain string
    if (slot && typeof slot === "object") return slot.time ?? null;
    return slot ?? null;
  };

  const buildCalendarIso = (dateValue, timeValue) => {
    const dateKey = getCalendarDateKey(dateValue);
    const parsedTime = timeValue
      ? moment(timeValue, ["HH:mm", "hh:mm A"], true)
      : null;
    const timePart =
      parsedTime && parsedTime.isValid()
        ? parsedTime.format("HH:mm:ss.SSS")
        : "00:00:00.000";
    return moment(`${dateKey}T${timePart}`).utc().toISOString();
  };

  let userid = profile?.user?.id;
  const form = useForm({
    initialValues: {
      quantity: "0",
      reason: "",
      comments: "",
      outcome: "",
    },
  });

  const compareDate = (date, medicationType, upcoming) => {
    var time1 = moment(date).startOf("day");
    var today = moment().startOf("day");
    if (
      calendar !== "weekly" &&
      calendar !== "monthly" &&
      medicationType === upcoming
    ) {
      return -1;
    } else if (time1.isSame(today)) {
      return 0;
    } else if (time1.isAfter(today)) {
      return 1;
    } else {
      return -1;
    }
  };

  const selectMedicineHandler = (_v) => {
    // Use _id + timeSlot as compound key so two slots of the same MAR document
    // (which share the same _id when timeSlot is stored as an array) can be
    // selected/deselected independently.
    if (
      selectedMedicine?.filter(
        (val) =>
          val?._id == _v?._id &&
          getSlotKey(val?.timeSlot) == getSlotKey(_v?.timeSlot),
      )?.length > 0
    ) {
      let filteredMed = selectedMedicine?.filter(
        (val) =>
          !(
            val?._id == _v?._id &&
            getSlotKey(val?.timeSlot) == getSlotKey(_v?.timeSlot)
          ),
      );
      setSelectedMedicine(filteredMed);
    } else {
      setSelectedMedicine([...selectedMedicine, _v]);
    }
  };

  useEffect(() => {
    if (opened) {
      form.setFieldValue("quantity", String(data?.dosage));
    }
  }, []);

  const [tempDate, setTempDate] = useState(
    form.values.createdAt ? new Date(form.values.createdAt) : null,
  );
  const [tempTime, setTempTime] = useState(
    form.values.createdAt ? dayjs(form.values.createdAt).format("HH:mm") : "",
  );

  const [validation, setValidation] = useState({
    comments: (value) => (value ? null : "Required"),
  });

  useEffect(() => {
    setValidation((prevValidation) => ({
      ...prevValidation,
    }));
  }, [form?.values?.comments]);

  const handleDateUpdate = (newDate) => {
    setTempDate(newDate);
    if (newDate) {
      const dateTime = new Date(newDate);
      if (tempTime) {
        const [hours, minutes] = tempTime.split(":");
        dateTime.setHours(parseInt(hours), parseInt(minutes));
      } else {
        const now = new Date();
        dateTime.setHours(now.getHours(), now.getMinutes());
        setTempTime(
          `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
        );
      }
      form.setFieldValue("createdAt", dateTime);
    } else {
      form.setFieldValue("createdAt", null);
    }
  };

  const handleTimeUpdate = (newTime) => {
    setTempTime(newTime);
    if (tempDate && newTime) {
      const [hours, minutes] = newTime.split(":");
      const dateTime = new Date(tempDate);
      dateTime.setHours(parseInt(hours), parseInt(minutes));
      form.setFieldValue("createdAt", dateTime);
    }
  };

  const onSave = async (values, type) => {
    const uniqueArray = selectedMedicine?.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t) => t?.medicationId === value?.medicationId),
    );
    let medicationName = null;
    let redFlag = false;
    let i = 0;
    do {
      let filterMedication = selectedMedicine?.filter(
        (item) => item?.medicationId == uniqueArray[i]?.medicationId,
      );
      let count = 0;
      filterMedication?.map((item) => {
        count += Number(item?.form3?.medicationadministered);
      });

      if (count > uniqueArray[i]?.form3?.medicineRemaining) {
        redFlag = true;
        medicationName = uniqueArray[i]?.form3?.medicationName;
      }
      i++;
    } while (i < uniqueArray?.length && !redFlag);

    if (redFlag && type === "Taken") {
      swal({
        title: `${medicationName} is overfilled`,
        text: "Medication Overfilled",
        icon: "info",
        dangerMode: true,
        buttons: ["Cancel", "Ok"],
      });
    } else if (!redFlag || type != "Taken") {
      const sortMedicine = (data) => {
        return data.sort((a, b) => {
          const timeA =
            a?.medicationType !== "PRN"
              ? moment(a?.timeSlot, "hh:mm A").format("HH:mm")
              : moment(a?.createdAt).format("HH:mm");
          const timeB =
            b?.medicationType !== "PRN"
              ? moment(b?.timeSlot, "hh:mm A").format("HH:mm")
              : moment(b?.createdAt).format("HH:mm");

          return timeA.localeCompare(timeB);
        });
      };

      const sortedMedicine = sortMedicine(selectedMedicine);

      const getCreatedAt = (item) => {
        if (form.values?.createdAt) {
          return buildCalendarIso(
            form.values.createdAt,
            moment(form.values.createdAt).format("HH:mm"),
          );
        }

        // No date/time selected: use today's date + medication's own slot time.
        // The date is always today (the day the nurse is marking the status),
        // NOT the scheduled calendar date of the medication.
        if (item?.timeSlot) {
          return buildCalendarIso(new Date(), getSlotKey(item.timeSlot));
        }

        // Last fallback: today + current time
        return buildCalendarIso(new Date(), dayjs().format("HH:mm"));
      };

      let payload = {
        client: selectedClient?.id,
        medicationType: data?.form3?.medicationType === "PRN" ? "PRN" : type,
        date:
          calendar === "weekly" || calendar === "monthly"
            ? getCalendarDateKey(selectedDate)
            : getCalendarDateKey(date),
        createdAt: getCreatedAt(),
        staff: selectedStaff || userid,
        medicationDetails: [],
        included: [],
        comments: form?.values?.comments,
      };

      sortedMedicine?.forEach((data) => {
        // Per-medication staff: the row's own pick, else the drawer-level
        // default, else the signed-in user. Sent per detail so the backend
        // stamps each administration with the person who actually gave it.
        const rowStaff = getRowStaff(data) || userid;
        payload.medicationDetails.push({
          marId: data?._id,
          medicationId: data?.medicationId,
          dosage: data?.form3?.medicationadministered
            ? Number(data?.form3?.medicationadministered)
            : 0,
          slot: getSlotKey(data?.timeSlot),
          createdAt: getCreatedAt(data),
          comments: data?.comments,
          staff: rowStaff,
          staffName: getStaffLabel(rowStaff),
        });
      });

      let allSortedMedicine = [];
      if (calendar === "weekly" || calendar === "monthly") {
        allSortedMedicine = sortMedicine(filteredWeekData);
      } else {
        allSortedMedicine = sortMedicine(data);
      }

      function convertTo24HourFormat(time) {
        if (!time) return "00:00";
        const match = time.match(/(\d+):(\d+)\s*(AM|PM)/);
        if (!match) return "00:00";
        const [, hour, minute, period] = match;
        let hour24 = parseInt(hour);

        if (period === "AM" && hour24 === 12) {
          hour24 = 0; // Midnight case (12:00 AM -> 00:00)
        } else if (period === "PM" && hour24 !== 12) {
          hour24 += 12; // PM times, except for 12 PM, need 12 added
        }

        if (hour24 < 10) {
          return `0${hour24}:${minute}`;
        } else {
          return `${hour24}:${minute}`;
        }
      }

      // Function to get filtered data for multiple selections
      function getFilteredDataForMultiple(data, selectedObjects) {
        const results = [];

        selectedObjects.forEach((selectedObject) => {
          const { medicationId, slot } = selectedObject;
          const selectedTime = convertTo24HourFormat(slot);
          // Carry the row's staff onto its `included` entries — the Taken branch
          // on the backend writes the medicationType entry from `included`, so
          // without this the per-medication choice would be lost for Taken.
          const rowStaff = selectedObject?.staff;
          const rowStaffName = selectedObject?.staffName;

          // Only match the exact selected time slot — no cascade to future slots.
          // Previously ">= selectedTime" caused all future slots of the same
          // medication to be included and saved, even when not selected.
          const filtered = data
            .filter(
              (_fitem) =>
                _fitem.medicationId === medicationId &&
                convertTo24HourFormat(_fitem.timeSlot) === selectedTime,
            )
            .map((_mitem) => ({
              ..._mitem,
              newMedicationType: type,
            }));

          results.push(
            ...filtered.map((_f) => ({
              marId: _f?._id,
              medicationId: _f?.medicationId,
              dosage: Number(_f?.form3?.medicationadministered) || 0,
              slot: getSlotKey(_f?.timeSlot),
              newMedicationType: _f?.newMedicationType,
              createdAt: getCreatedAt(_f),
              staff: rowStaff,
              staffName: rowStaffName,
            })),
          );
        });

        // Remove duplicates from the results.
        // Use marId + slot as the compound key so two time slots in the same
        // MAR document (same marId, different slot) are both kept.
        return results.filter(
          (obj, index, self) =>
            index ===
            self.findIndex((o) => o.marId === obj.marId && o.slot === obj.slot),
        );
      }

      const myResult = getFilteredDataForMultiple(
        allSortedMedicine,
        payload?.medicationDetails,
      );

      const sortMars = (data) => {
        return data.sort((a, b) => {
          const timeA =
            a?.medicationType !== "PRN"
              ? moment(a?.slot, "hh:mm A").format("HH:mm")
              : moment(a?.createdAt).format("HH:mm");
          const timeB =
            b?.medicationType !== "PRN"
              ? moment(b?.slot, "hh:mm A").format("HH:mm")
              : moment(b?.createdAt).format("HH:mm");

          return timeA.localeCompare(timeB);
        });
      };

      const sortResult = sortMars(myResult);

      payload.included = sortResult;

      setBtnLoading(true);
      try {
        const res = await createMar(payload);
        if (res) {
          let calendar_view = localStorage.getItem("calendar_date");
          getMar(calendar_view, selectedClient?.id);
          form.reset();
          setSelectedMedicine([]);
          setMedStaff({});
          close();
        }
      } catch (error) {}
      setBtnLoading(false);

      // } else {
      //   alert("Quantity is Required!");
      // }
    }
  };

  useEffect(() => {
    if (calendar === "monthly") {
      const todayKey = moment().format("YYYY-MM-DD");
      const todayInArray = date?.find(
        (item) => getCalendarDateKey(item) === todayKey,
      );
      handleDateChange(todayInArray ?? currentDate);
    } else {
      handleDateChange(currentDate);
    }
  }, [data]);

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setSelectedMedicine([]);
    setMedStaff({});
    const filteredWeekData = data?.filter(
      (item) => getCalendarDateKey(item?.date) == getCalendarDateKey(value),
    );
    setFilteredData(filteredWeekData);
  };

  const { staff } = useSelector((state) => state?.staff);
  const [selectedStaff, setSelectedStaff] = useState();

  const assignedStaffIds = selectedClient?.staff
    ?.filter((s) => s?.isAssign === true)
    ?.map((s) => s?.staffId);

  const populateStaff = staff?.staffs
    ?.filter((staff) => assignedStaffIds?.includes(staff?.id))
    ?.map((staff) => ({
      label: staff?.name,
      value: staff?.id,
    }));

  // Options for the per-medication "Administered By" pickers.
  // The signed-in user is prepended when they are not part of the assigned-staff
  // list, so the implicit default (`selectedStaff || userid`) is visible and
  // re-selectable instead of the field rendering blank.
  const staffOptions = (() => {
    const opts = Array.isArray(populateStaff) ? [...populateStaff] : [];
    const currentId = profile?.user?.id;
    if (currentId && !opts.some((o) => o?.value === currentId)) {
      opts.unshift({
        value: currentId,
        label: profile?.user?.name || "Me",
      });
    }
    return opts;
  })();

  // Only admins/owners may record on someone else's behalf — a staff user always
  // signs their own administrations, so the pickers stay hidden for them.
  const canChooseStaff = profile?.user?.role !== "staff";

  // ── Per-medication administering staff ──────────────────────────────────────
  // Keyed by the same compound key used for selection (_id + slot) so two slots of
  // the same MAR document keep independent staff. A row with no entry here falls
  // back to the drawer-level picker: that is what makes "Select all" default every
  // row to the current staff while each row remains individually editable, and it
  // keeps one row's change from touching any other row.
  const [medStaff, setMedStaff] = useState({});

  const getMedKey = (_v) => `${_v?._id}__${getSlotKey(_v?.timeSlot) ?? ""}`;

  const getRowStaff = (_v) => medStaff[getMedKey(_v)] ?? selectedStaff ?? null;

  const setRowStaff = (_v, staffId) =>
    setMedStaff((prev) => ({ ...prev, [getMedKey(_v)]: staffId }));

  const getStaffLabel = (id) =>
    staffOptions?.find((o) => o?.value === id)?.label || "";

  // const [populateStaff, setPopulateStaff] = useState();

  // useEffect(() => {
  //   const staffOptions =
  //     staff?.staffs?.map((item) => ({
  //       value: item?.id,
  //       label: item?.name,
  //     })) || [];

  //   const defaultStaff = profile?.user?.id
  //     ? {
  //         value: profile?.user?.id,
  //         label: profile?.user?.name,
  //       }
  //     : null;

  //   // Add default staff if not already in the list
  //   const isDefaultInList = staffOptions.some(
  //     (opt) => opt.value === defaultStaff?.value
  //   );
  //   const finalOptions =
  //     defaultStaff && !isDefaultInList
  //       ? [defaultStaff, ...staffOptions]
  //       : staffOptions;

  //   setPopulateStaff(finalOptions);
  //   setSelectedStaff(defaultStaff?.value || null);
  // }, [staff, profile]);

  const [selectedStatus, setSelectedStatus] = useState(null);

  const handleClose = () => {
    setSelectedMedicine([]);
    setMedStaff({});
    setSelectedStatus(null);
    form.reset();
    close();
  };

  const handleSave = () => {
    if (selectedStatus !== "Taken") {
      const commentError = validation.comments(form.values.comments);
      if (commentError) {
        form.setFieldError("comments", commentError);
        return;
      }
    }
    onSave(form.values, selectedStatus);
  };

  const isFuture = dayjs.utc(date).isAfter(dayjs.utc());

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => {
          handleClose();
        }}
        closeOnClickOutside={false}
        offset={8}
        size={690}
        sx={{
          ".mantine-Drawer-close": {
            color: "var(--app-text)",
            "& svg": {
              width: "25px !important",
              height: "25px !important",
            },
          },
          ".mantine-Drawer-content": {
            background: "transparent",
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          },
          ".mantine-Drawer-close:hover": {
            color: "var(--app-primary)",
          },
          ".mantine-1q36a81": {
            padding: "0",
          },
          ".mantine-Drawer-body": {
            padding: "22px",
            background: "var(--app-surface-2)",
          },
        }}
        zIndex={1000}
        position="right"
        overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
        shadow="md"
        title={
          <Text fz={24} fw={700} ff="Inter" color="var(--app-text)">
            Medication
          </Text>
        }
      >
        {/* ── Staff + Date/Time ── */}
        {canChooseStaff && (
          <Select
            data={staffOptions}
            value={selectedStaff}
            placeholder="Select Staff"
            label="Select Staff"
            description="Default for every medication you select — each one can be changed individually below."
            onChange={(e) => setSelectedStaff(e)}
            rightSection={<IconChevronDown />}
            nothingFound="No staff found"
            className={classes.inputStyle}
          />
        )}
        <Box className={classes.contentBox}>
          {(calendar === "weekly" || calendar === "monthly") && (
            <Select
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              nothingFound="No medication found"
              data={date?.map((item) => ({
                value: item,
                label: moment(item).format(
                  calendar === "monthly"
                    ? "MMM DD, YYYY (ddd)"
                    : "dddd (MMM DD, YYYY)",
                ),
              }))}
              placeholder="Select a date"
              rightSection={<IconChevronDown />}
              searchable={calendar === "monthly"}
              className={classes.inputStyle}
            />
          )}
          <Flex gap="md" mt={8}>
            <DatePickerInput
              label="Administration Date"
              placeholder="Pick a date"
              value={tempDate}
              onChange={handleDateUpdate}
              maxDate={new Date()}
              icon={<FiCalendar />}
              clearable
              className={classes.inputStyle}
            />
            <TimeInput
              label="Administration Time"
              placeholder="Pick a time"
              value={tempTime}
              onChange={(e) => handleTimeUpdate(e.currentTarget.value)}
              icon={<IconClock />}
              format="12"
              withDropdown
              className={`${classes.inputStyle}`}
            />
          </Flex>

          <Box className={classes.medScrollBox}>
            {/* ── Select medication subheader ── */}
            <Flex className={classes.medScrollBoxHdr}>
              <p className="medScrollBoxHdrHdng">Select medication</p>
              <Flex align="center" gap={10}>
                <Button className={classes.prnButton} onClick={openPRN}>
                  Administer PRN
                </Button>
                <Radio
                  label="Select all"
                  size="xs"
                  color="yellow"
                  disabled={isFuture || compareDate(selectedDate) === 1}
                  checked={(() => {
                    const filtered =
                      calendar === "weekly" || calendar === "monthly"
                        ? filteredWeekData
                        : data;
                    const _effDate =
                      calendar === "weekly" || calendar === "monthly"
                        ? selectedDate
                        : date;
                    const selectable = filtered?.filter((_v) => {
                      const st = getMedStatusForDate(_v, _effDate);
                      if (st === "Missed") return true;
                      if (st === "upcoming")
                        return moment(_effDate)
                          .startOf("day")
                          .isSameOrBefore(moment().startOf("day"));
                      return false;
                    });
                    return (
                      selectable?.length > 0 &&
                      selectable?.every((med) =>
                        selectedMedicine?.some(
                          (s) =>
                            s?._id === med?._id &&
                            getSlotKey(s?.timeSlot) ===
                              getSlotKey(med?.timeSlot),
                        ),
                      )
                    );
                  })()}
                  onChange={() => {
                    const filtered =
                      calendar === "weekly" || calendar === "monthly"
                        ? filteredWeekData
                        : data;
                    const _effDate =
                      calendar === "weekly" || calendar === "monthly"
                        ? selectedDate
                        : date;
                    const selectable = filtered?.filter((_v) => {
                      const st = getMedStatusForDate(_v, _effDate);
                      if (st === "Missed") return true;
                      if (st === "upcoming")
                        return moment(_effDate)
                          .startOf("day")
                          .isSameOrBefore(moment().startOf("day"));
                      return false;
                    });
                    const allSelected = selectable?.every((med) =>
                      selectedMedicine?.some(
                        (s) =>
                          s?._id === med?._id &&
                          getSlotKey(s?.timeSlot) === getSlotKey(med?.timeSlot),
                      ),
                    );
                    if (allSelected) {
                      setSelectedMedicine(
                        selectedMedicine?.filter(
                          (s) =>
                            !selectable?.some(
                              (sel) =>
                                sel?._id === s?._id &&
                                getSlotKey(sel?.timeSlot) ===
                                  getSlotKey(s?.timeSlot),
                            ),
                        ),
                      );
                    } else {
                      const next = [...selectedMedicine];
                      selectable?.forEach((med) => {
                        if (
                          !next?.some(
                            (s) =>
                              s?._id === med?._id &&
                              getSlotKey(s?.timeSlot) ===
                                getSlotKey(med?.timeSlot),
                          )
                        )
                          next.push(med);
                      });
                      setSelectedMedicine(next);
                    }
                  }}
                />
              </Flex>
            </Flex>

            {/* ── Medication cards ── */}
            <Box className={classes.medBody}>
              {(calendar === "weekly" || calendar === "monthly"
                ? filteredWeekData
                : data
              )?.length > 0 ? (
                (calendar === "weekly" || calendar === "monthly"
                  ? filteredWeekData
                  : data
                )
                  ?.sort((a, b) => new Date(a?.date) - new Date(b?.date))
                  ?.map((v, i) => {
                    const _effectiveDate =
                      calendar === "weekly" || calendar === "monthly"
                        ? selectedDate
                        : date;
                    const _resolvedStatus = getMedStatusForDate(
                      v,
                      _effectiveDate,
                    );
                    const isDisabled =
                      isFuture ||
                      compareDate(date) === 1 ||
                      compareDate(selectedDate) === 1 ||
                      futureDate === 1 ||
                      _resolvedStatus === "Taken" ||
                      _resolvedStatus === "Refused" ||
                      _resolvedStatus === "Unable to Take" ||
                      _resolvedStatus === "Out of Medication" ||
                      _resolvedStatus === "PRN";
                    const isChecked = selectedMedicine.some(
                      (val) =>
                        val?._id === v?._id &&
                        getSlotKey(val?.timeSlot) === getSlotKey(v?.timeSlot),
                    );
                    return (
                      <div key={i} className={classes.cardWrapper}>
                        <div
                          className={classes.selectionDot}
                          style={{
                            backgroundColor: isChecked
                              ? "var(--app-primary)"
                              : isDisabled
                                ? "var(--app-border)"
                                : "var(--app-border)",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                          onClick={() =>
                            !isDisabled && selectMedicineHandler(v)
                          }
                        />
                        <Box className={classes.cardStack}>
                          <Card
                            data={v}
                            allData={data}
                            calendar={calendar}
                            date={_effectiveDate}
                          />
                          {/* Per-medication "Administered By" — shown once the
                              row is selected so each medication in a bulk
                              administration can be signed by its own staff. */}
                          {isChecked && canChooseStaff && (
                            <Flex className={classes.rowStaffBar}>
                              <Text className={classes.rowStaffLabel}>
                                Administered By
                              </Text>
                              <Select
                                size="xs"
                                data={staffOptions}
                                value={getRowStaff(v)}
                                onChange={(val) => setRowStaff(v, val)}
                                placeholder="Select staff"
                                disabled={btnLoading}
                                rightSection={<IconChevronDown size={14} />}
                                nothingFound="No staff found"
                                searchable
                                className={classes.rowStaffSelect}
                                aria-label={`Administered by for ${
                                  v?.form3?.medicationName || "medication"
                                }`}
                              />
                            </Flex>
                          )}
                        </Box>
                      </div>
                    );
                  })
              ) : (
                <Center style={{ height: "200px" }}>
                  <Text ff="inter" c="dimmed" fw={500} fz={16}>
                    No Medication Found
                  </Text>
                </Center>
              )}
            </Box>
          </Box>

          {/* ── Comment ── */}
          <Box mt={10}>
            <Textarea
              id="dosage-completed"
              label="Comment"
              disabled={selectedMedicine?.length === 0 || btnLoading}
              placeholder="Enter comments here"
              defaultValue={form.values.comments}
              {...form.getInputProps("comments")}
              className={classes.inputStyle}
            />
          </Box>

          {/* ── Status radio buttons ── */}
          <Box mt={14}>
            <Radio.Group
              value={selectedStatus}
              onChange={setSelectedStatus}
              name="medicationStatus"
            >
              <Flex gap={16} wrap="wrap" justify="center">
                <Flex align="center" gap={4}>
                  <Radio
                    value="Taken"
                    label={
                      <Flex
                        className={classes.statusLabel}
                        style={{ color: getColor("Taken")?.dark }}
                      >
                        <img src={takenMed} alt="taken" />
                        Taken
                      </Flex>
                    }
                    color="green"
                  />
                </Flex>
                <Flex align="center" gap={4}>
                  <Radio
                    value="Refused"
                    label={
                      <Flex
                        className={classes.statusLabel}
                        style={{ color: getColor("Refused")?.dark }}
                      >
                        <img src={refusedMed} alt="refused" />
                        Refused
                      </Flex>
                    }
                    color="blue"
                  />
                </Flex>
                <Flex align="center" gap={4}>
                  <Radio
                    value="Unable to Take"
                    label={
                      <Flex
                        className={classes.statusLabel}
                        style={{ color: getColor("Unable to Take")?.dark }}
                      >
                        <img src={unableToTakeMed} alt="unable" />
                        Unable to take
                      </Flex>
                    }
                    color="yellow"
                  />
                </Flex>
                <Flex align="center" gap={4}>
                  <Radio
                    value="Out of Medication"
                    label={
                      <Flex
                        className={classes.statusLabel}
                        style={{ color: getColor("Out of Medication")?.dark }}
                      >
                        <img src={outOfMed} alt="out" />
                        Out of medication
                      </Flex>
                    }
                    color="gray"
                    disabled={futureDate === 1}
                  />
                </Flex>
              </Flex>
            </Radio.Group>
          </Box>

          {/* ── Save button ── */}
          <Box mt={14} pb={20}>
            <Button
              fullWidth
              loading={btnLoading}
              disabled={
                selectedMedicine?.length === 0 || !selectedStatus || btnLoading
              }
              onClick={handleSave}
              className={classes.saveBtn}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Drawer>
      {prnShow && (
        <PRNMedicationModal
          isNew={isNew}
          ref={prnRef}
          opened={prnShow}
          onclose={prnClose}
          data={selectedClient}
          date={date}
        />
      )}
    </>
  );
};

export const Card = ({ data, allData, calendar, date }) => {
  const { classes } = useStyles();
  const _medicationType = getMedStatusForDate(data, date);

  const getCalendarDateKey = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))
      return value;
    if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value))
      return moment(value, "MM/DD/YYYY").format("YYYY-MM-DD");
    return moment(value).format("YYYY-MM-DD");
  };
  const { selectedClient } = useSelector((state) => state.mar);

  const [opened, { open, close }] = useDisclosure(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [prnShow, setPRNShow] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const prnRef = useRef();

  const compareDate = (date) => {
    const time1 = moment(date).startOf("day");
    const today = moment().startOf("day");
    if (time1.isAfter(today)) return 1;
    if (
      calendar !== "weekly" &&
      calendar !== "monthly" &&
      _medicationType === "upcoming"
    )
      return -1;
    if (time1.isSame(today)) return 0;
    return -1;
  };

  const openPRNView = (d) => {
    setIsNew(false);
    prnRef?.current?.openView(d, d?.date);
    setPRNShow(true);
  };
  const prnClose = () => {
    setSelectedRow(null);
    setPRNShow(false);
  };

  const medName = data?.form3?.medicationName || "";
  const dosage = data?.form3?.dosage
    ? `${data.form3.dosage}${data.form3.unit || ""}`
    : "";

  const _strengthAndForm = data?.form3?.strengthAndForm || "";

  const medNameDisplay = dosage
    ? `${medName} (${dosage || _strengthAndForm || ""})`
    : medName;
  const routeDisplay = [data?.form3?.ingestionMode, data?.form3?.dosageForm]
    .filter(Boolean)
    .join(" ");

  const qtyValue = (() => {
    const mt = data?.medicationType;
    if (Array.isArray(mt) && mt.length > 0) {
      const entrySlot = getActiveSlotForDate(data?.timeSlot, data?.date);
      for (const targetDate of [date, data?.date]) {
        if (!targetDate) continue;
        const targetDateStr = getCalendarDateKey(targetDate);
        const match =
          mt
            .filter(
              (m) =>
                getCalendarDateKey(m.date) === targetDateStr &&
                m.timeSlot === entrySlot,
            )
            .pop() ||
          mt.filter((m) => getCalendarDateKey(m.date) === targetDateStr).pop();
        if (match?.dosage != null) return match.dosage;
      }
    }
    return _medicationType === "Taken"
      ? data?.dosage
      : data?.form3?.medicationadministered;
  })();
  const remainingValue =
    data?.medicineRemaining === "unlimited"
      ? null
      : data?.medicineRemaining == null || data?.medicineRemaining === "null"
        ? "0.0"
        : Number(data?.medicineRemaining).toFixed(1);
  const totalValue =
    data?.medicineRemaining !== "unlimited"
      ? Number(data?.totalMedicine).toFixed(1)
      : null;

  const statusColor = getColor(_medicationType);

  const cardAdministeredAt = (() => {
    const mt = data?.medicationType;
    if (!Array.isArray(mt) || mt.length === 0) return data?.createdAt;
    const entrySlot = getActiveSlotForDate(data?.timeSlot, data?.date);
    const targetDateStr = getCalendarDateKey(date || data?.date);
    const exact = mt.filter(
      (m) =>
        getCalendarDateKey(m.date) === targetDateStr &&
        m.timeSlot === entrySlot,
    );
    if (exact.length)
      return (
        exact[exact.length - 1].administeredAt || exact[exact.length - 1].date
      );
    const dateOnly = mt.filter(
      (m) => getCalendarDateKey(m.date) === targetDateStr && !m.timeSlot,
    );
    if (dateOnly.length)
      return (
        dateOnly[dateOnly.length - 1].administeredAt ||
        dateOnly[dateOnly.length - 1].date
      );
    const last = mt[mt.length - 1];
    return last.administeredAt || last.date || data?.createdAt;
  })();

  return (
    <>
      <div
        className={classes.newCard}
        onClick={() => {
          setSelectedRow(data);
          if (data?.form3?.medicationType === "PRN") return openPRNView(data);
          if (compareDate(date) === 1) {
            setIsViewOpen(false);
          } else {
            _medicationType === "upcoming" || _medicationType === "Missed"
              ? setIsViewOpen(false)
              : setIsViewOpen(true);
            open();
          }
        }}
      >
        {/* Top row */}
        <Flex
          className={classes.newCardTop}
          align="flex-end"
          justify="space-between"
        >
          <div className={classes.newCardFreq}>
            <Text className="freq">{data?.form3?.frequency || "-"}</Text>
            <Text className="time">
              {_medicationType === "PRN"
                ? cardAdministeredAt
                  ? moment(cardAdministeredAt).format("h:mm A")
                  : "-"
                : getActiveSlotForDate(data?.timeSlot, data?.date) || data?.timeSlot || "-"}
            </Text>
          </div>
          <div className={classes.newCardMed}>
            <Flex>
              <Text className="name">{medNameDisplay}</Text>
              {data?.form3?.isNarcotic === "Yes" && (
                <span className={classes.narcoticBadge}>Narcotic</span>
              )}
            </Flex>
            {routeDisplay && <Text className="route">{routeDisplay}</Text>}
          </div>
          {_medicationType !== "Taken" ? (
            ""
          ) : (
            <div className={classes.newCardQty}>
              <Text className="val">{qtyValue ?? "-"}</Text>
              <Text className="lbl">Quantity dispensed</Text>
            </div>
          )}
          <div className={classes.newCardRemaining}>
            {data?.medicineRemaining === "unlimited" ||
            data?.totalMedicine === "unlimited" ? (
              <Box className={classes.unlimitedBadge}>
                <Text className={classes.unlimitedText}></Text>Unlimited
              </Box>
            ) : (
              <Text className="val">
                {remainingValue} of {totalValue}
              </Text>
            )}
            <Text className="lbl">Drug Remaining</Text>
          </div>
        </Flex>

        {/* Bottom row */}
        <Flex
          className={classes.newCardBottom}
          align="center"
          justify="space-between"
          style={{ backgroundColor: _medicationType === "PRN" ? "var(--app-tint-purple)" : statusColor?.light || "var(--app-surface)" }}
        >
          <div>
            <Text className={classes.newCardBottomLabel}>Recorded</Text>
            <Text className={classes.newCardBottomValue}>
              {_medicationType === "Missed" || _medicationType === "upcoming"
                ? "—"
                : data?.staff?.name || "—"}
            </Text>
          </div>
          <div>
            <Text className={classes.newCardBottomLabel}>Time</Text>
            <Text className={classes.newCardBottomValue}>
              {_medicationType === "Missed" || _medicationType === "upcoming"
                ? "—"
                : moment(cardAdministeredAt).format("h:mm A")}
            </Text>
          </div>
          <Flex align="center" gap={6}>
            <img
              src={getStatusImage(_medicationType)}
              style={{ height: 18, width: 36 }}
              alt={_medicationType}
            />
            <Text
              fz={11}
              fw={600}
              style={{ color: statusColor?.dark || "var(--app-text)" }}
            >
              {_medicationType === "upcoming" ? "Upcoming" : _medicationType}
            </Text>
          </Flex>
        </Flex>
      </div>

      {/* ── Individual medication detail modal — outside clickable div to prevent event bubbling ── */}
      <PRNMedicationModal
        isNew={isNew}
        ref={prnRef}
        opened={prnShow}
        onclose={prnClose}
        data={selectedClient}
        date={date}
      />
      {selectedRow && (
        <MedicationModal
          opened={opened}
          onclose={() => {
            close();
            setIsViewOpen(false);
            setSelectedRow(null);
          }}
          isViewOpen={isViewOpen}
          setIsViewOpen={setIsViewOpen}
          data={selectedRow}
          date={date}
          allData={allData}
        />
      )}
    </>
  );
};

export default MarMedicationModal;
